import { makeAutoObservable, runInAction } from 'mobx'
import { type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import {
  type IResponseCountryState,
  type IResponseEinItem,
} from 'entities/Compliance/api/types/complianceType'
import { ComplianceApi } from 'entities/Compliance'
import { type IResponseCountry } from 'entities/Auth'
import { AuthApi } from 'entities/Auth/api'
import { ZIP_CODE_CONSTANTS, getZipCodeValidationError } from 'pages/settings/lib/businessAddress'

const MAX_ZIP_LENGTH = ZIP_CODE_CONSTANTS.USA.MAX_LENGTH
const MAX_POST_CODE_LENGTH = ZIP_CODE_CONSTANTS.CANADA.MAX_LENGTH

export type IAddressData = {
  street1: string
  street2: string
  city: string
  state: string
  zip: string
  country: string
}

export type IAddressFieldsError = {
  country?: string
  street_address_1?: string
  street_address_2?: string
  city?: string
  zip_code?: string
  postal_code?: string
  state_id?: string
}

export type IBusinessAddressFormStoreConfig = {
  onFieldChange?: (field: keyof IAddressData, value: string) => void
  deleteErrorValue?: (keyName: string) => void
}

export class BusinessAddressFormStore {
  private _addressData: IResponseEinItem | null = null
  private _activeCountryId = ''
  private _activeCountryLabel: string | null = ''
  private _states: IResponseCountryState[] = []
  private _countries: IResponseCountry[] = []
  private _zipLocalErrorMessage: string | null = null
  private _config: IBusinessAddressFormStoreConfig = {}
  private _loading = false

  constructor(config?: IBusinessAddressFormStoreConfig) {
    makeAutoObservable(this)
    if (config) {
      this._config = config
    }
  }

  init = async () => {
    this._loading = true
    try {
      await Promise.all([this.loadStates(), this.loadCountries()])
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get loading() {
    return this._loading
  }

  get addressData() {
    return this._addressData
  }

  get activeCountryId() {
    return this._activeCountryId
  }

  get activeCountryLabel() {
    return (this._activeCountryLabel = this.countries.find(
      (country) => country.id === this.activeCountryId
    )?.label as string)
  }

  get isActiveCountryUS() {
    return this._activeCountryLabel === 'United States'
  }

  get isActiveCountryCA() {
    return this._activeCountryLabel === 'Canada'
  }

  get isUsaLocation() {
    return this._activeCountryId === 'US'
  }

  get isCanadaLocation() {
    return this._activeCountryId === 'CA'
  }

  get countries(): IDropdownItem[] {
    return this._countries.map((item) => ({ label: item.label, id: item.key }))
  }

  get states(): IDropdownItem[] {
    return this._states
      .filter((item) => item.country_id === this._activeCountryId)
      .map((item) => ({ label: item.name, id: item.id }))
  }

  get activeCountryInfo() {
    if (this.isUsaLocation) {
      return {
        placeholder: 'e.g. Los Angeles',
        state: 'State',
        statePlaceholder: 'Select',
        code: 'ZIP code',
        codePlaceholder: 'e.g. 90001',
      }
    }
    if (this.isCanadaLocation) {
      return {
        placeholder: 'e.g. Toronto',
        state: 'Province',
        statePlaceholder: 'Select',
        code: 'Postal code',
        codePlaceholder: 'e.g. A1A 1A1',
      }
    }
    return {
      placeholder: 'Enter city',
      state: 'Region',
      statePlaceholder: 'Enter region',
      code: 'Postal code',
      codePlaceholder: 'Enter postal code',
    }
  }

  get zipLocalErrorMessage() {
    return this._zipLocalErrorMessage
  }

  get zipCode() {
    const zip = this._addressData?.zip

    if (this.isUsaLocation || !zip) {
      return zip
    }

    if (this.isCanadaLocation) {
      return zip.length < 4 ? zip : `${zip.slice(0, 3)} ${zip.slice(3)}`
    }

    return zip
  }

  get isZipFilled() {
    if (this.isUsaLocation) {
      return this._addressData?.zip?.length === MAX_ZIP_LENGTH
    }
    if (this.isCanadaLocation) {
      return this._addressData?.zip?.length === MAX_POST_CODE_LENGTH
    }
    return (this._addressData?.zip?.length as number) >= 1
  }

  get isAddressFilled() {
    const keys = ['city', 'state', 'street1'] as const

    if (!this.isZipFilled) return false

    return keys.every((key) => this._addressData?.[key]?.length)
  }

  get isNextDisabled() {
    return !this._activeCountryId || !this.isAddressFilled
  }

  setAddressData = (value: IResponseEinItem | null) => {
    this._addressData = value
  }

  clearAddressFields = () => {
    if (this._addressData) {
      this._addressData.street1 = ''
      this._addressData.street2 = ''
      this._addressData.city = ''
      this._addressData.state = ''
      this._addressData.zip = ''
    }

    this._config.onFieldChange?.('street1', '')
    this._config.onFieldChange?.('street2', '')
    this._config.onFieldChange?.('city', '')
    this._config.onFieldChange?.('state', '')
    this._config.onFieldChange?.('zip', '')
  }

  setActiveCountry = (countryId: string, clearFields?: boolean) => {
    this._activeCountryId = countryId
    this._config.deleteErrorValue?.('country')
    this.resetAddressErrors()
    clearFields && this.clearAddressFields()
    this._config.onFieldChange?.('country', countryId)
  }

  setStreet1 = (street: string) => {
    this._config.deleteErrorValue?.('street_address_1')

    if (!this._addressData) {
      this._addressData = {} as IResponseEinItem
    }
    this._addressData.street1 = street
    this._config.onFieldChange?.('street1', street)
  }

  setStreet2 = (street: string) => {
    this._config.deleteErrorValue?.('street_address_2')

    if (!this._addressData) {
      this._addressData = {} as IResponseEinItem
    }
    this._addressData.street2 = street
    this._config.onFieldChange?.('street2', street)
  }

  setCity = (city: string) => {
    this._config.deleteErrorValue?.('city')

    if (!this._addressData) {
      this._addressData = {} as IResponseEinItem
    }
    this._addressData.city = city
    this._config.onFieldChange?.('city', city)
  }

  setState = (state: string) => {
    this._config.deleteErrorValue?.('state_id')
    if (!this._addressData) {
      this._addressData = {} as IResponseEinItem
    }
    this._addressData.state = state
    this._config.onFieldChange?.('state', state)
  }

  setZip = (zip: string) => {
    if (!this._addressData) {
      this._addressData = {} as IResponseEinItem
    }

    this._config.deleteErrorValue?.('zip_code')
    this._config.deleteErrorValue?.('postal_code')
    this.setZipLocalErrorMessage(null)

    if (this.isUsaLocation) {
      const clearedZip = zip.replace(/[^0-9]/g, '')

      if (clearedZip.length > MAX_ZIP_LENGTH) return

      this._addressData.zip = clearedZip
    } else if (this.isCanadaLocation) {
      const clearedZip = zip.replace(/\s+/g, '')

      if (clearedZip.length > MAX_POST_CODE_LENGTH) return

      this._addressData.zip = clearedZip
    } else {
      this._addressData.zip = zip
    }

    this._config.onFieldChange?.('zip', this._addressData.zip)
  }

  setZipLocalErrorMessage = (message: string | null) => {
    this._zipLocalErrorMessage = message
  }

  checkZipLength = () => {
    const error = getZipCodeValidationError({
      zipLength: this._addressData?.zip?.length,
      isUsaLocation: this.isUsaLocation,
      isCanadaLocation: this.isCanadaLocation,
    })
    this.setZipLocalErrorMessage(error)
  }

  resetAddressErrors = () => {
    this._config.deleteErrorValue?.('street_address_1')
    this._config.deleteErrorValue?.('street_address_2')
    this._config.deleteErrorValue?.('city')
    this._config.deleteErrorValue?.('state_id')
    this._config.deleteErrorValue?.('zip_code')
    this._config.deleteErrorValue?.('postal_code')
    this.setZipLocalErrorMessage(null)
  }

  reset = () => {
    this._addressData = null
    this._activeCountryId = ''
    this._activeCountryLabel = ''
    this._zipLocalErrorMessage = null
  }

  setConfig = (config: IBusinessAddressFormStoreConfig) => {
    this._config = config
  }

  private setStates = (states: IResponseCountryState[]) => {
    this._states = states
  }

  private setCountries = (countries: IResponseCountry[]) => {
    this._countries = countries
  }

  private loadStates = async () => {
    try {
      const { data } = await ComplianceApi.getBpStates()
      this.setStates(data)
    } catch (e) {
      logger.error(e)
    }
  }

  private loadCountries = async () => {
    try {
      const { data } = await AuthApi.getCountries()
      this.setCountries(data)
    } catch (e) {
      logger.error(e)
    }
  }
}
